import {
  getCustomerApplicationsRetrieveQueryKey,
  useCustomerApplicationsPartialUpdate,
} from "@boundless/bl-api-client/customer/services"
import { zodResolver } from "@hookform/resolvers/zod"
import { useQueryClient } from "@tanstack/react-query"
import { useForm } from "react-hook-form"
import { toast } from "sonner"
import { z } from "zod"

import { FormInput } from "@/components/form-elements/FormInput"
import { Form } from "@/components/ui/form"
import type { ErrorType } from "@/lib/clients"
import { applyErrorsToForm, type ErrorResponseType } from "@/lib/forms/utils"
import { getExistingAverageBalance } from "@/lib/integrations/ondeck"

import type { CustomerApplications } from "@boundless/bl-api-client/customer/models"

export function ExtraDataCopy() {
  return (
    <p>
      Additionally, the lender(s) you have selected require the following
      information:
    </p>
  )
}

const formSchema = z.object({
  averageBalance: z.preprocess(
    (val) => (val === "" ? undefined : Number(val)),
    z.number().min(0).max(2147483647),
  ),
})

export function OnDeckExtraDataForm({
  application,
  companyUuid,
}: {
  application: CustomerApplications
  companyUuid: string
}) {
  const queryClient = useQueryClient()

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      averageBalance: getExistingAverageBalance(application.extraData),
    },
  })

  const { mutate: submitExtraData, isPending } =
    useCustomerApplicationsPartialUpdate({
      mutation: {
        onSuccess: (applicationData) => {
          const queryKey = getCustomerApplicationsRetrieveQueryKey(
            companyUuid,
            application.uuid,
          )
          queryClient.setQueryData(queryKey, applicationData)

          toast.success("Additional information updated")
        },
        onError: (error) => {
          if (error.status === 400) {
            applyErrorsToForm(
              form.setError,
              error as ErrorType<ErrorResponseType>,
            )
          }
        },
      },
    })

  const handleSubmit = (data: z.infer<typeof formSchema>) => {
    submitExtraData({
      uuid: application.uuid,
      companyUuid,
      data: { extraData: data },
    })
  }

  return (
    <Form {...form}>
      <form
        className="flex flex-col gap-6"
        onSubmit={form.handleSubmit(handleSubmit)}
      >
        <ExtraDataCopy />
        <div className="flex flex-col">
          <FormInput
            name={"averageBalance"}
            placeholder={"Average Balance"}
            type="number"
            helpText="Average balance across last 3 months"
            onBlurCapture={form.handleSubmit(handleSubmit)}
            disabled={isPending}
          />
        </div>
      </form>
    </Form>
  )
}
